import styled from '@emotion/styled'
import { Modal, Typography } from '@mui/material'
import { FC, useEffect, useMemo, useState } from 'react'
import { Colors } from 'src/components/theme'
import { Close } from '@emotion-icons/material/Close'
import { CardNumberElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { StripeElementsOptions } from '@stripe/stripe-js'
import getStripePromise from 'src/views/subscriptions/create/get-stripe'
import { billingClient } from 'src/clients'
import { CreditCardForm as BaseCreditCardForm } from 'src/components/credit-card-form'
import { Alert } from 'src/components/alert/alert'
import { Button } from 'src/components/button'

const Container = styled.div`
  width: 1086px;
  height: 680px;
  background-color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 8px;
  padding: 40px;
  display: flex;
  flex-direction: column;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 510px;
  align-self: center;
  height: 100%;
`

const TopContent = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
`

const Spacer = styled.div`
  flex: 1;
`

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 16px;
`

const Description = styled(Typography)`
  margin-top: 16px;
`

const CancelButton = styled(Button)`
  border-color: 1px solid ${Colors.blueDark};
  color: ${Colors.blueDark};
  padding: 6px 12px !important;
`

const SaveButton = styled(Button)`
  background-color: ${Colors.blueDark};
  color: white;
  padding: 6px 12px !important;

  &:hover {
    background-color: ${Colors.blueDark};
    opacity: 0.9;
  }
`

type HeaderProps = {
  onClose?: () => void
}

const Header: FC<HeaderProps> = ({ onClose }) => (
  <HeaderWrapper>
    <HeaderTitle>
      <Typography fontSize={24} fontWeight="bold">
        Add New Card
      </Typography>
      <Typography variant="textmdm" color={Colors.textMedium}>
        Fill the form below to add your credit card
      </Typography>
    </HeaderTitle>
    <Close size={32} color={Colors.neutral700} onClick={onClose} cursor="pointer" />
  </HeaderWrapper>
)

type FormProps = {
  onCancel?: () => void
  onSuccess?: () => void
}
const InnerForm: FC<FormProps & { clientSecret: string }> = ({ onCancel, onSuccess, clientSecret }) => {
  const elements = useElements()
  const stripe = useStripe()
  const [error, setError] = useState<string>('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async () => {
    if (!elements || !stripe || loading) {
      return
    }

    const cardNumberElement = elements.getElement(CardNumberElement)
    if (!cardNumberElement) {
      return
    }

    setError('')
    setLoading(true)

    try {
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardNumberElement,
        },
      })

      if (result.error) {
        setError(result.error.message ?? 'An unexpected error occurred.')
        return
      }

      onSuccess?.()
    } catch (err) {
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const renderTopContent = () => (
    <>
      {error && (
        <TopContent>
          <Alert message={error} color="error" />
        </TopContent>
      )}
    </>
  )

  const bottomContent = () => (
    <>
      <Description color={Colors.textMedium} fontSize={14}>
        By providing your card information, you allow AlphaMap to charge your card for future payments in accordance
        with their terms.
      </Description>
      <Spacer />
      <ButtonsWrapper>
        <CancelButton variant="outlined" onClick={onCancel} fullWidth>
          Cancel
        </CancelButton>
        <SaveButton color="primary" fullWidth type="submit">
          {loading ? 'Loading...' : 'Save'}
        </SaveButton>
      </ButtonsWrapper>
    </>
  )

  return <BaseCreditCardForm onSubmit={handleSubmit} topContent={renderTopContent()} bottomContent={bottomContent()} />
}
const Form: FC<FormProps> = ({ onCancel, onSuccess }) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null)

  useEffect(() => {
    const fetchClientSecret = async () => {
      try {
        const clientSecret = await billingClient.createPaymentMethodIntent()
        setClientSecret(clientSecret)
      } catch {
        setClientSecret(null)
      }
    }
    void fetchClientSecret()
  }, [setClientSecret])

  const stripePromise = useMemo(async () => {
    return await getStripePromise()
  }, [])

  if (!clientSecret || typeof clientSecret === 'object') {
    return null
  }

  const options: StripeElementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      disableAnimations: true,
    },
  }

  return (
    <Elements options={options} stripe={stripePromise}>
      <InnerForm clientSecret={clientSecret} onCancel={onCancel} onSuccess={onSuccess} />
    </Elements>
  )
}

type CreditCardFormProps = {
  open: boolean
  onClose?: () => void
  onSuccess?: () => void
}

export const CreditCardForm: FC<CreditCardFormProps> = ({ open, onClose, onSuccess }) => {
  return (
    <Modal open={open}>
      <Container>
        <Header onClose={onClose} />
        {open && (
          <Content>
            <Form onCancel={onClose} onSuccess={onSuccess} />
          </Content>
        )}
      </Container>
    </Modal>
  )
}
